if (!customElements.get('sw-float-scene')) {
  const EASE = 0.07;
  const EPSILON = 0.05;

  class SwFloatScene extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-depth]'));
      if (!this.items.length) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Targets are set by input; current values ease toward them every frame so
      // the pointer entering or leaving the section never snaps.
      this.target = { x: 0, y: 0, scroll: 0 };
      this.current = { x: 0, y: 0, scroll: 0 };
      this.running = false;
      this.visible = false;

      this.onScroll = () => {
        this.readScroll();
        this.start();
      };
      this.onPointerMove = (event) => {
        const rect = this.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        this.target.x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
        this.target.y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
        this.start();
      };
      this.onPointerLeave = () => {
        this.target.x = 0;
        this.target.y = 0;
        this.start();
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerleave', this.onPointerLeave);
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
          this.toggleAttribute('data-sw-off', !this.visible);
          if (this.visible) {
            this.readScroll();
            this.start();
          }
        },
        { rootMargin: '20% 0px' }
      );
      this.observer.observe(this);

      this.readScroll();
      this.current.scroll = this.target.scroll;
      this.apply();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.observer) this.observer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.running = false;
    }

    readScroll() {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      const travel = (viewport + rect.height) / 2;
      // -1 while the section is below the fold, +1 once it has scrolled past.
      this.target.scroll = Math.max(-1, Math.min(1, (viewport / 2 - (rect.top + rect.height / 2)) / travel));
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.frame = requestAnimationFrame(() => this.tick());
    }

    tick() {
      this.current.x += (this.target.x - this.current.x) * EASE;
      this.current.y += (this.target.y - this.current.y) * EASE;
      this.current.scroll += (this.target.scroll - this.current.scroll) * EASE;

      this.apply();

      const settled =
        Math.abs(this.target.x - this.current.x) < EPSILON / 100 &&
        Math.abs(this.target.y - this.current.y) < EPSILON / 100 &&
        Math.abs(this.target.scroll - this.current.scroll) < EPSILON / 100;

      if (settled || !this.visible) {
        this.running = false;
        return;
      }

      this.frame = requestAnimationFrame(() => this.tick());
    }

    apply() {
      for (const item of this.items) {
        const depth = parseFloat(item.dataset.depth) || 0;
        const shiftY = this.current.scroll * depth * 64 + this.current.y * depth * -9;
        const shiftX = this.current.x * depth * -13;
        item.style.setProperty('--sw-shift-y', `${shiftY.toFixed(2)}px`);
        item.style.setProperty('--sw-shift-x', `${shiftX.toFixed(2)}px`);
      }
    }
  }

  customElements.define('sw-float-scene', SwFloatScene);
}
